import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { emailSearchVariants } from '../utils/emailSearch';

interface LenderEmailEntry {
  email_or_domain: string;
  lender_name: string;
  added_by: string;
  added_at: string;
}

interface ReclassificationSummary {
  moved?: number;
  failed?: number;
  deferred?: number;
}

interface Toast {
  message: string;
  tone: 'success' | 'warning';
}

interface AuditEntry {
  action: 'add' | 'remove';
  email_or_domain: string;
  lender_name: string;
  performed_by: string;
  performed_at: string;
}

interface LenderEmailFilterProps {
  isOpen: boolean;
  onClose: () => void;
}

const LenderEmailFilter: React.FC<LenderEmailFilterProps> = ({ isOpen, onClose }) => {
  const { apiRequest } = useAuth();

  const [entries, setEntries] = useState<LenderEmailEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  // Auto-dismiss toast after 5s. Keep a ref to the timeout so consecutive saves
  // don't leave stale timers running.
  const toastTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((message: string, tone: 'success' | 'warning' = 'success') => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToast({ message, tone });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 5000);
  }, []);
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  // Add form state
  const [newEmailOrDomain, setNewEmailOrDomain] = useState('');
  const [newLenderName, setNewLenderName] = useState('');
  const [adding, setAdding] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Track which entries are currently being deleted
  const [deletingEntries, setDeletingEntries] = useState<Set<string>>(new Set());

  // Audit log state
  const [showHistory, setShowHistory] = useState(false);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<{ entries: LenderEmailEntry[] }>({
        method: 'GET',
        url: '/ui/admin/lender-emails',
      });
      const list = data?.entries ?? (Array.isArray(data) ? data : []);
      setEntries(list);
    } catch (err: any) {
      console.error('Failed to fetch lender emails:', err);
      setError(err.response?.data?.error || 'Failed to fetch lender email list');
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  const fetchAuditLog = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await apiRequest<{ entries: AuditEntry[] }>({
        method: 'GET',
        url: '/ui/admin/lender-emails/audit',
      });
      setAuditEntries(response?.entries ?? []);
    } catch (err) {
      console.error('Failed to fetch audit log', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    if (showHistory) {
      fetchAuditLog();
    }
  }, [showHistory, fetchAuditLog]);

  useEffect(() => {
    if (isOpen) {
      fetchEntries();
      // Reset form state when modal opens
      setNewEmailOrDomain('');
      setNewLenderName('');
      setSearchTerm('');
      setError(null);
      setToast(null);
      setShowHistory(false);
      setAuditEntries([]);
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    }
  }, [isOpen, fetchEntries]);

  const handleAdd = async () => {
    const trimmedEmail = newEmailOrDomain.trim();
    const trimmedLender = newLenderName.trim();

    if (!trimmedEmail) {
      setError('Please enter an email address or @domain');
      return;
    }
    if (!trimmedLender) {
      setError('Please enter a lender name');
      return;
    }

    setAdding(true);
    setError(null);

    try {
      const response = await apiRequest<{ reclassification?: ReclassificationSummary }>({
        method: 'POST',
        url: '/ui/admin/lender-emails',
        data: {
          email_or_domain: trimmedEmail,
          lender_name: trimmedLender,
        },
      });
      const recl = response?.reclassification;
      const moved = recl?.moved ?? 0;
      const deferred = recl?.deferred ?? 0;
      if (deferred > 0 && moved > 0) {
        showToast(
          `Added ${trimmedEmail} - ${moved} tasks moved to Lender, ${deferred} deferred (over cap)`,
          'warning',
        );
      } else if (deferred > 0) {
        showToast(
          `Added ${trimmedEmail} - ${deferred} matching tasks deferred (over cap)`,
          'warning',
        );
      } else if (moved > 0) {
        showToast(
          `Added ${trimmedEmail} - ${moved} tasks moved to Lender`,
          'success',
        );
      } else {
        showToast(`Added ${trimmedEmail} - no pending tasks matched`, 'success');
      }
      setNewEmailOrDomain('');
      setNewLenderName('');
      await fetchEntries();
      if (showHistory) {
        fetchAuditLog();
      }
    } catch (err: any) {
      console.error('Failed to add lender email:', err);
      setError(err.response?.data?.error || 'Failed to add lender email entry');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (emailOrDomain: string) => {
    setDeletingEntries((prev) => new Set(prev).add(emailOrDomain));
    setError(null);

    try {
      const response = await apiRequest<{ reclassification?: ReclassificationSummary }>({
        method: 'DELETE',
        url: `/ui/admin/lender-emails/${encodeURIComponent(emailOrDomain)}`,
      });
      const recl = response?.reclassification;
      const moved = recl?.moved ?? 0;
      const deferred = recl?.deferred ?? 0;
      if (deferred > 0 && moved > 0) {
        showToast(
          `Removed ${emailOrDomain} - ${moved} tasks moved back to Client, ${deferred} deferred (over cap)`,
          'warning',
        );
      } else if (deferred > 0) {
        showToast(
          `Removed ${emailOrDomain} - ${deferred} matching tasks deferred (over cap)`,
          'warning',
        );
      } else if (moved > 0) {
        showToast(
          `Removed ${emailOrDomain} - ${moved} tasks moved back to Client`,
          'success',
        );
      } else {
        showToast(`Removed ${emailOrDomain} - no tasks were affected`, 'success');
      }
      setEntries((prev) => prev.filter((e) => e.email_or_domain !== emailOrDomain));
      if (showHistory) {
        fetchAuditLog();
      }
    } catch (err: any) {
      console.error('Failed to delete lender email:', err);
      setError(err.response?.data?.error || `Failed to delete "${emailOrDomain}"`);
    } finally {
      setDeletingEntries((prev) => {
        const next = new Set(prev);
        next.delete(emailOrDomain);
        return next;
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !adding) {
      handleAdd();
    }
  };

  const filteredEntries = useMemo(() => {
    if (!searchTerm.trim()) return entries;
    const lower = searchTerm.toLowerCase();
    // gmail.com and googlemail.com are the same mailbox: match the email/domain
    // field against both domain variants of the search term.
    const emailVariants = emailSearchVariants(searchTerm);
    return entries.filter(
      (e) =>
        emailVariants.some((v) => e.email_or_domain.toLowerCase().includes(v)) ||
        e.lender_name.toLowerCase().includes(lower)
    );
  }, [entries, searchTerm]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative mx-4 flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Lender Email Filter</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage known lender email addresses and domains used by the email classifier.
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close modal"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {toast && (
          <div
            role="status"
            className={`mx-6 mt-4 rounded-md border px-4 py-3 text-sm ${
              toast.tone === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-teal-200 bg-teal-50 text-teal-800'
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Add New Entry */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">Add New Entry</h3>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Email Address or @Domain
              </label>
              <input
                type="text"
                value={newEmailOrDomain}
                onChange={(e) => setNewEmailOrDomain(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. replies@lender.com or @lender.com"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                disabled={adding}
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Lender Name
              </label>
              <input
                type="text"
                value={newLenderName}
                onChange={(e) => setNewLenderName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. Black Horse Finance"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                disabled={adding}
              />
            </div>
            <button
              onClick={handleAdd}
              disabled={adding || !newEmailOrDomain.trim() || !newLenderName.trim()}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-teal-500 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {adding ? (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Adding...
                </>
              ) : (
                'Add'
              )}
            </button>
          </div>
        </div>

        {/* Search / Filter */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter by email, domain, or lender name..."
              className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm placeholder-gray-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </div>
          <div className="mt-2 text-xs text-gray-400">
            {loading
              ? 'Loading...'
              : `${filteredEntries.length} of ${entries.length} entries`}
          </div>
        </div>

        {/* Entries List */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
          <div className="mb-3 flex items-center justify-end">
            <button
              type="button"
              onClick={() => setShowHistory((prev) => !prev)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
            >
              {showHistory ? 'Hide history' : 'Show history'}
            </button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="h-8 w-8 animate-spin text-teal-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="ml-3 text-sm text-gray-500">Loading lender emails...</span>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="py-12 text-center">
              <svg className="mx-auto h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5-1.885a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75a2.25 2.25 0 012.25-2.25h15a2.25 2.25 0 012.25 2.25v9" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                {searchTerm ? 'No entries match your filter.' : 'No lender emails registered yet.'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="pb-2 pr-3">Email / Domain</th>
                  <th className="pb-2 pr-3">Lender</th>
                  <th className="hidden pb-2 pr-3 sm:table-cell">Added By</th>
                  <th className="hidden pb-2 pr-3 md:table-cell">Date Added</th>
                  <th className="pb-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEntries.map((entry) => {
                  const isDeleting = deletingEntries.has(entry.email_or_domain);
                  return (
                    <tr
                      key={entry.email_or_domain}
                      className={`text-sm transition-colors ${
                        isDeleting ? 'bg-red-50 opacity-60' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="py-2.5 pr-3">
                        <span className="font-mono text-sm text-gray-800">
                          {entry.email_or_domain}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-gray-700">{entry.lender_name}</td>
                      <td className="hidden py-2.5 pr-3 text-gray-500 sm:table-cell">
                        {entry.added_by || '--'}
                      </td>
                      <td className="hidden py-2.5 pr-3 text-gray-500 md:table-cell">
                        {entry.added_at ? formatDate(entry.added_at) : '--'}
                      </td>
                      <td className="py-2.5 text-right">
                        <button
                          onClick={() => handleDelete(entry.email_or_domain)}
                          disabled={isDeleting}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                          title={`Remove ${entry.email_or_domain}`}
                          aria-label={`Remove ${entry.email_or_domain}`}
                        >
                          {isDeleting ? (
                            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {showHistory && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-gray-500">
                Audit log
              </h3>
              {historyLoading && (
                <div className="text-sm text-gray-500">Loading...</div>
              )}
              {!historyLoading && auditEntries.length === 0 && (
                <div className="text-sm text-gray-500">No audit entries yet.</div>
              )}
              {!historyLoading && auditEntries.length > 0 && (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      <th className="pb-2 pr-3">When</th>
                      <th className="pb-2 pr-3">Action</th>
                      <th className="pb-2 pr-3">Email / Domain</th>
                      <th className="pb-2 pr-3">Lender</th>
                      <th className="pb-2">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {auditEntries.map((entry, idx) => (
                      <tr key={`${entry.performed_at}-${idx}`} className="text-sm">
                        <td className="whitespace-nowrap py-2 pr-3 text-gray-600">
                          {formatDate(entry.performed_at)}
                        </td>
                        <td className="py-2 pr-3 font-medium">
                          <span
                            className={
                              entry.action === 'add' ? 'text-green-700' : 'text-amber-700'
                            }
                          >
                            {entry.action}
                          </span>
                        </td>
                        <td className="py-2 pr-3 font-mono text-gray-800">
                          {entry.email_or_domain}
                        </td>
                        <td className="py-2 pr-3 text-gray-700">{entry.lender_name}</td>
                        <td className="py-2 text-gray-600">{entry.performed_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-gray-200 px-6 py-3">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LenderEmailFilter;
